import { Button } from "@photo-booth/ui/button";
import { analyticsClick } from "@/lib/analytics/props";
import { cn } from "@photo-booth/ui";
import type { JobStatusResponseDto } from "@/api";
import { useResultDownload } from "@/hooks/useResultDownload";

interface ResultActionsProps {
  viewMode: "image" | "gif";
  jobStatus?: JobStatusResponseDto;
  onReportIssue: () => void;
  onExploreMore: () => void;
}

/**
 * Action buttons for the result page
 * Includes: Download, Report Issue, and Explore More buttons
 */
export const ResultActions: React.FC<ResultActionsProps> = ({
  jobStatus,
  viewMode,
  onReportIssue,
  onExploreMore,
}) => {
  const { isDownloadingImage, isDownloadingGif, downloadImage, downloadGif } = useResultDownload({
    jobStatus,
    delayBeforeCallback: 2000,
  });
  const isDownloading = viewMode === "image" ? isDownloadingImage : isDownloadingGif;
  const download = viewMode === "image" ? downloadImage : downloadGif;

  return (
    <div className={cn(
      "grid grid-cols-2 gap-4",
      jobStatus?.gifPath ? 'mt-12' : 'mt-16'
    )}>
      <Button
        className="flex flex-col gap-3"
        variant="ghost"
        onClick={download}
        disabled={isDownloading}
        {...analyticsClick({
          event: "download-result-button",
          feature: "ui",
          location: window.location.pathname,
        })}
      >
        <img src="/save-to-camera-roll.png" alt="Download" className="size-6" />
        <p className="opacity-80 text-[16px]">
          {isDownloading ? "Downloading..." : `download ${viewMode}`}
        </p>
      </Button>
      
      <Button
        className="flex flex-col gap-3"
        variant="ghost"
        onClick={onReportIssue}
        {...analyticsClick({
          event: "report-issue-button",
          feature: "ui",
          location: window.location.pathname,
        })}
      >
        <img src="/social/line-outline.png" alt="Line" width={30} height={30} />
        <p className="opacity-80 text-[16px]">Report Issue via Line</p>
      </Button>
      
      <div className="col-span-2 mt-6">
        <Button 
          className="w-full flex flex-col gap-3" 
          variant="ghost" 
          onClick={onExploreMore}
        >
          <img src="/explore-another-frame.png" alt="Back to home" className="w-6 h-auto" />
          <p className="opacity-80 text-[16px]">Back to home</p>
        </Button>
      </div>
    </div>
  );
};

